import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { HealthBar } from '../../HealthBar.js';
import { PALETTE, matte, colored } from '../../Style.js';

const GOLD = 0xd6a640;
const STEEL = 0xaab0b8;
const STEEL_DARK = 0x6c7079;
const VISOR = 0x1d1c22;
const LEATHER = 0x5a3420;

// Horse leg pivot height (shoulder/hip); the hoof touches the ground below it
const LEG_LENGTH = 1.2;

// A box stretched between two points, for limbs, reins and poles.
function beam(from, to, thickness, color) {
  const a = new THREE.Vector3(...from);
  const b = new THREE.Vector3(...to);
  const dir = b.clone().sub(a);
  const geo = colored(new THREE.BoxGeometry(thickness, thickness, dir.length()), color);
  const quat = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 0, 1), dir.normalize());
  geo.applyMatrix4(new THREE.Matrix4().compose(a.add(b).multiplyScalar(0.5), quat, new THREE.Vector3(1, 1, 1)));
  return geo;
}

// A flat cross emblem facing +Z (or -Z when flipped).
function cross(x, y, z, size, color) {
  return [
    colored(new THREE.BoxGeometry(size * 0.28, size, 0.03), color, { x, y, z }),
    colored(new THREE.BoxGeometry(size * 0.75, size * 0.28, 0.03), color, { x, y: y + size * 0.12, z })
  ];
}

// Lancer (tank): a mounted knight on a warhorse draped in a team-coloured
// caparison, carrying a raised lance. The Paladin (super tank) rides a barded white
// destrier in full plate with a great helm, gold-trimmed tabard and banner.
export class Tank {
  constructor(scene, unit, color, isSuper = false) {
    this.scene = scene;
    this.unit = unit;
    this.color = color;
    this.isSuper = isSuper;
    this.mesh = null;
    this.turretGroup = null; // Separate group for turret rotation (the rider's upper body)
    this.bodyGroup = null;
    this.legs = [];
    this.geometries = [];
    this.gaitPhase = 0;
    this.gaitAmount = 0;
    this.lastPosition = null;
    this.lastMeshPosition = null;
    this.targetRotation = 0;
    this.turretTargetRotation = 0;
    this.currentTurretRotation = 0;
    this.healthBar = null;
    this.maxHealth = isSuper ? 90 : 30; // SuperTankHealth vs TankHealth
    this.create();
  }

  create() {
    // Scale factor for super tanks (1.3x larger)
    const scale = this.isSuper ? 1.3 : 1.0;
    const material = matte(0xffffff, { vertexColors: true });

    this.mesh = new THREE.Group();

    // The model is built with the ground at y=0, which is where the unit's position is
    const model = new THREE.Group();
    model.scale.setScalar(scale);
    this.mesh.add(model);

    // Everything above the legs bobs with the gait
    this.bodyGroup = new THREE.Group();
    model.add(this.bodyGroup);

    const body = new THREE.Mesh(this.track(mergeGeometries(this.buildHorse())), material);
    body.castShadow = true;
    body.receiveShadow = true;
    this.bodyGroup.add(body);

    // Legs are separate meshes so they can swing at the shoulder and hip
    const legGeometry = this.track(mergeGeometries(this.buildLeg()));
    for (const [x, z, pair] of [[-0.28, 0.8, 0], [0.28, 0.8, 1], [-0.28, -0.85, 1], [0.28, -0.85, 0]]) {
      const leg = new THREE.Mesh(legGeometry, material);
      leg.position.set(x, LEG_LENGTH, z);
      leg.castShadow = true;
      leg.userData.pair = pair; // Diagonal pairs move together, as in a trot
      model.add(leg);
      this.legs.push(leg);
    }

    // Create turret group for independent rotation: the rider turns at the hips
    this.turretGroup = new THREE.Group();
    this.turretGroup.position.set(0, 2.15, -0.15);
    this.bodyGroup.add(this.turretGroup);

    const rider = new THREE.Mesh(this.track(mergeGeometries(this.buildRider())), material);
    rider.castShadow = true;
    this.turretGroup.add(rider);

    // Create health bar above tank
    this.healthBar = new HealthBar(this.maxHealth, 3 * scale, 0.35);
    this.healthBar.getGroup().position.y = 4.8 * scale;
    this.mesh.add(this.healthBar.getGroup());

    // Set initial position
    this.mesh.position.set(this.unit.position.x, this.unit.position.y, this.unit.position.z);
    this.lastPosition = { ...this.unit.position };
    this.lastMeshPosition = this.mesh.position.clone();

    this.scene.add(this.mesh);
  }

  track(geometry) {
    this.geometries.push(geometry);
    return geometry;
  }

  // The horse's body, head and tack, plus the rider's legs in the stirrups
  buildHorse() {
    const team = this.color;
    const coat = this.isSuper ? 0xd6d0c4 : 0x7a4a2a;
    const mane = this.isSuper ? 0x9a948a : 0x2e2018;
    const hem = this.isSuper ? GOLD : PALETTE.cloth;
    const parts = [
      // Barrel draped in the team-coloured caparison
      colored(new THREE.SphereGeometry(1, 7, 5), team, { y: 1.55, scale: [0.5, 0.48, 1.2] }),
      // Neck, head, muzzle and ears
      colored(new THREE.BoxGeometry(0.38, 1.0, 0.5), coat, { y: 2.15, z: 1.0, rx: 0.6 }),
      colored(new THREE.BoxGeometry(0.32, 0.32, 0.75), coat, { y: 2.6, z: 1.48, rx: 1.05 }),
      colored(new THREE.BoxGeometry(0.28, 0.24, 0.2), mane, { y: 2.34, z: 1.63, rx: 1.05 }),
      colored(new THREE.ConeGeometry(0.06, 0.2, 4), coat, { x: -0.09, y: 2.98, z: 1.28 }),
      colored(new THREE.ConeGeometry(0.06, 0.2, 4), coat, { x: 0.09, y: 2.98, z: 1.28 }),
      // Mane and tail
      colored(new THREE.BoxGeometry(0.1, 0.95, 0.12), mane, { y: 2.29, z: 0.79, rx: 0.6 }),
      colored(new THREE.BoxGeometry(0.16, 0.85, 0.18), mane, { y: 1.45, z: -1.28, rx: 0.3 }),
      // Saddle with pommel and cantle
      colored(new THREE.BoxGeometry(0.55, 0.14, 0.7), LEATHER, { y: 2.06, z: -0.15 }),
      colored(new THREE.BoxGeometry(0.3, 0.18, 0.1), LEATHER, { y: 2.17, z: 0.22 }),
      colored(new THREE.BoxGeometry(0.45, 0.3, 0.1), LEATHER, { y: 2.2, z: -0.5 })
    ];

    // Caparison skirts hanging to the knees, with a contrasting hem
    for (const side of [-1, 1]) {
      parts.push(colored(new THREE.BoxGeometry(0.06, 0.62, 2.3), team, { x: side * 0.47, y: 1.15 }));
      parts.push(colored(new THREE.BoxGeometry(0.08, 0.08, 2.32), hem, { x: side * 0.47, y: 0.86 }));
    }
    for (const z of [-1.12, 1.08]) {
      parts.push(colored(new THREE.BoxGeometry(0.9, 0.62, 0.06), team, { y: 1.15, z }));
      parts.push(colored(new THREE.BoxGeometry(0.92, 0.08, 0.08), hem, { y: 0.86, z }));
    }

    // Reins from the bit back to the pommel
    for (const side of [-1, 1]) {
      parts.push(beam([side * 0.15, 2.35, 1.62], [side * 0.14, 2.25, 0.25], 0.03, LEATHER));
    }

    // Rider's armoured legs in the stirrups
    const leg = this.isSuper ? STEEL : STEEL_DARK;
    for (const side of [-1, 1]) {
      const hip = [side * 0.2, 2.15, -0.15];
      const knee = [side * 0.42, 1.85, 0.15];
      const ankle = [side * 0.46, 1.38, 0.0];
      parts.push(beam(hip, knee, 0.2, leg));
      parts.push(beam(knee, ankle, 0.17, leg));
      parts.push(colored(new THREE.BoxGeometry(0.16, 0.1, 0.3), STEEL_DARK, { x: ankle[0], y: ankle[1] - 0.04, z: ankle[2] + 0.06 }));
    }

    if (this.isSuper) {
      // Steel barding: crinet over the neck and a chanfron with a spike
      parts.push(colored(new THREE.BoxGeometry(0.44, 0.9, 0.3), STEEL, { y: 2.25, z: 0.91, rx: 0.6 }));
      parts.push(colored(new THREE.BoxGeometry(0.36, 0.14, 0.66), STEEL, { y: 2.73, z: 1.43, rx: 1.05 }));
      parts.push(colored(new THREE.BoxGeometry(0.06, 0.16, 0.62), GOLD, { y: 2.77, z: 1.41, rx: 1.05 }));
      parts.push(colored(new THREE.ConeGeometry(0.05, 0.3, 4), GOLD, { y: 2.98, z: 1.5, rx: 0.5 }));
      // Gold crosses on the caparison flanks
      for (const side of [-1, 1]) {
        parts.push(...cross(0, 1.2, 0, 0.42, GOLD).map(g => g.rotateY(side * Math.PI / 2).translate(side * 0.51, 0, 0)));
      }
    }

    return parts;
  }

  // One horse leg hanging from its pivot: upper leg, cannon and hoof
  buildLeg() {
    const coat = this.isSuper ? 0xd6d0c4 : 0x7a4a2a;
    const parts = [
      colored(new THREE.BoxGeometry(0.2, 0.6, 0.24), coat, { y: -0.3 }),
      colored(new THREE.BoxGeometry(0.15, 0.5, 0.17), coat, { y: -0.85 }),
      colored(new THREE.BoxGeometry(0.2, 0.12, 0.22), 0x2a2420, { y: -LEG_LENGTH + 0.06 })
    ];
    if (this.isSuper) {
      // Feathered fetlocks
      parts.push(colored(new THREE.BoxGeometry(0.2, 0.14, 0.22), 0xf1ece0, { y: -1.0 }));
    }
    return parts;
  }

  // The rider from the hips up, with the lance leaning along local +Z (the aiming axis)
  buildRider() {
    const team = this.color;
    const trim = this.isSuper ? GOLD : PALETTE.cloth;
    const parts = [
      // Breastplate under a team-coloured tabard
      colored(new THREE.BoxGeometry(0.5, 0.62, 0.32), STEEL, { y: 0.38 }),
      colored(new THREE.BoxGeometry(0.54, 0.5, 0.36), team, { y: 0.3 }),
      colored(new THREE.BoxGeometry(0.58, 0.26, 0.42), team, { y: 0.0 }),
      colored(new THREE.BoxGeometry(0.56, 0.07, 0.38), this.isSuper ? GOLD : LEATHER, { y: 0.1 }),
      // Pauldrons
      colored(new THREE.BoxGeometry(0.22, 0.16, 0.32), STEEL, { x: -0.33, y: 0.64 }),
      colored(new THREE.BoxGeometry(0.22, 0.16, 0.32), STEEL, { x: 0.33, y: 0.64 }),
      // Right arm gripping the lance, left arm behind the shield
      beam([0.32, 0.6, 0], [0.36, 0.36, 0.08], 0.14, STEEL),
      beam([0.36, 0.36, 0.08], [0.32, 0.39, 0.32], 0.12, STEEL),
      beam([-0.32, 0.6, 0], [-0.36, 0.36, 0.08], 0.14, STEEL)
    ];

    if (this.isSuper) {
      // Great helm with a gold cross on the face, crest and plume
      parts.push(colored(new THREE.CylinderGeometry(0.2, 0.2, 0.42, 8), STEEL, { y: 0.92 }));
      parts.push(colored(new THREE.CylinderGeometry(0.16, 0.2, 0.06, 8), STEEL, { y: 1.16 }));
      parts.push(colored(new THREE.BoxGeometry(0.3, 0.04, 0.05), VISOR, { y: 0.98, z: 0.19 }));
      parts.push(colored(new THREE.BoxGeometry(0.05, 0.3, 0.04), GOLD, { y: 0.9, z: 0.2 }));
      parts.push(colored(new THREE.BoxGeometry(0.05, 0.22, 0.4), GOLD, { y: 1.28 }));
      parts.push(colored(new THREE.SphereGeometry(1, 5, 4), team, { y: 1.36, z: -0.22, rx: -0.5, scale: [0.09, 0.12, 0.34] }));
      // Gold cross on the tabard, front and back
      parts.push(...cross(0, 0.32, 0.19, 0.3, GOLD));
      parts.push(...cross(0, 0.32, -0.19, 0.3, GOLD));
      // Banner on a pole at the back
      parts.push(colored(new THREE.CylinderGeometry(0.035, 0.035, 1.7, 5), PALETTE.woodDark, { y: 1.05, z: -0.28 }));
      parts.push(colored(new THREE.ConeGeometry(0.07, 0.2, 5), GOLD, { y: 1.98, z: -0.28 }));
      parts.push(colored(new THREE.BoxGeometry(0.75, 0.05, 0.05), GOLD, { y: 1.82, z: -0.28 }));
      parts.push(colored(new THREE.BoxGeometry(0.7, 0.7, 0.04), team, { y: 1.45, z: -0.3 }));
      parts.push(colored(new THREE.BoxGeometry(0.72, 0.07, 0.05), GOLD, { y: 1.1, z: -0.3 }));
      parts.push(...cross(0, 1.47, -0.33, 0.36, GOLD));
    } else {
      // Pointed bascinet with a visor slit and a team plume
      parts.push(colored(new THREE.CylinderGeometry(0.17, 0.19, 0.32, 7), STEEL, { y: 0.88 }));
      parts.push(colored(new THREE.ConeGeometry(0.18, 0.2, 7), STEEL, { y: 1.14 }));
      parts.push(colored(new THREE.BoxGeometry(0.22, 0.03, 0.04), VISOR, { y: 0.92, z: 0.18 }));
      parts.push(colored(new THREE.SphereGeometry(1, 5, 4), team, { y: 1.24, z: -0.16, rx: -0.6, scale: [0.08, 0.1, 0.3] }));
    }

    // Lance carried upright, leaning forward toward the aim (+Z) so it stays
    // inside the unit's collision footprint: shaft, vamplate, point and pennon
    const lanceLength = this.isSuper ? 2.05 : 2.25; // Tip stays just under the health bar
    const lean = 0.52;
    const along = (geo, t, back = 0) => geo.translate(0, t, back).rotateX(lean).translate(0.32, 0, 0.1);
    parts.push(along(colored(new THREE.CylinderGeometry(0.03, 0.07, lanceLength, 6), this.isSuper ? 0xf1ece0 : PALETTE.woodLight), lanceLength / 2));
    parts.push(along(colored(new THREE.ConeGeometry(0.17, 0.3, 7), this.isSuper ? GOLD : STEEL), 0.72));
    parts.push(along(colored(new THREE.ConeGeometry(0.06, 0.3, 4), STEEL), lanceLength + 0.15));
    parts.push(along(colored(new THREE.BoxGeometry(0.02, 0.45, 0.26), team), lanceLength - 0.35, -0.15));

    // Kite shield on the left arm, facing outward, in team colour
    const size = this.isSuper ? 1.2 : 1.0;
    const outline = [[-0.27, 0.32], [0.27, 0.32], [0.27, 0.02], [0, -0.5], [-0.27, 0.02]];
    const shape = new THREE.Shape(outline.map(([u, v]) => new THREE.Vector2(u * size, v * size)));
    parts.push(colored(new THREE.ExtrudeGeometry(shape, { depth: 0.06, bevelEnabled: false }), team, {
      ry: -Math.PI / 2, x: -0.4, y: 0.42, z: 0.08
    }));
    if (this.isSuper) {
      parts.push(...cross(0, 0, 0, 0.42, GOLD).map(g => g.rotateY(-Math.PI / 2).translate(-0.47, 0.5, 0.08)));
    } else {
      parts.push(colored(new THREE.BoxGeometry(0.02, 0.12, 0.5), trim, { x: -0.47, y: 0.5, z: 0.08 }));
    }

    return parts;
  }

  updatePosition(position) {
    if (this.mesh) {
      // Calculate body rotation based on movement direction
      if (this.lastPosition) {
        const dx = position.x - this.lastPosition.x;
        const dz = position.z - this.lastPosition.z;
        const distanceMoved = Math.sqrt(dx * dx + dz * dz);

        // Only update body rotation if we've moved significantly
        if (distanceMoved > 0.01) {
          this.targetRotation = Math.atan2(dx, dz);
        }
      }

      // Smoothly interpolate body rotation
      const currentRotation = this.mesh.rotation.y;
      let rotationDiff = this.targetRotation - currentRotation;

      // Handle rotation wrapping
      while (rotationDiff > Math.PI) rotationDiff -= Math.PI * 2;
      while (rotationDiff < -Math.PI) rotationDiff += Math.PI * 2;

      this.mesh.rotation.y += rotationDiff * 0.1;

      // Smoothly interpolate turret rotation (relative to body)
      if (this.turretGroup) {
        // Calculate turret rotation relative to body
        const relativeTargetRotation = this.turretTargetRotation - this.mesh.rotation.y;
        let turretDiff = relativeTargetRotation - this.turretGroup.rotation.y;

        // Handle rotation wrapping
        while (turretDiff > Math.PI) turretDiff -= Math.PI * 2;
        while (turretDiff < -Math.PI) turretDiff += Math.PI * 2;

        this.turretGroup.rotation.y += turretDiff * 0.15;
      }

      this.animateGait();

      // Store last position for next frame
      this.lastPosition = { x: position.x, y: position.y, z: position.z };
    }
  }

  // Trot driven by the distance the rendered horse actually travelled
  animateGait() {
    const scale = this.isSuper ? 1.3 : 1.0;
    const travelled = Math.hypot(
      this.mesh.position.x - this.lastMeshPosition.x,
      this.mesh.position.z - this.lastMeshPosition.z
    );
    this.lastMeshPosition.copy(this.mesh.position);

    const moving = travelled > 0.0001 && travelled < 5;
    if (moving) this.gaitPhase += (travelled / scale) * 2.6;
    // Ease the legs in and out so stopping doesn't freeze mid-stride
    this.gaitAmount += ((moving ? 1 : 0) - this.gaitAmount) * 0.1;

    for (const leg of this.legs) {
      const offset = leg.userData.pair ? Math.PI : 0;
      leg.rotation.x = Math.sin(this.gaitPhase + offset) * 0.55 * this.gaitAmount;
    }
    this.bodyGroup.position.y = Math.abs(Math.sin(this.gaitPhase)) * 0.09 * this.gaitAmount;
    this.bodyGroup.rotation.x = Math.sin(this.gaitPhase * 2) * 0.02 * this.gaitAmount;
  }

  // Set target position for turret to aim at
  setTarget(targetPosition) {
    if (this.mesh && targetPosition) {
      const dx = targetPosition.x - this.mesh.position.x;
      const dz = targetPosition.z - this.mesh.position.z;
      this.turretTargetRotation = Math.atan2(dx, dz);
    }
  }

  // Aim turret at a world position
  aimAt(worldX, worldZ) {
    if (this.mesh) {
      const dx = worldX - this.mesh.position.x;
      const dz = worldZ - this.mesh.position.z;
      this.turretTargetRotation = Math.atan2(dx, dz);
    }
  }

  // Update health bar
  updateHealth(health, camera) {
    if (this.healthBar) {
      this.healthBar.update(health, camera);
    }
  }

  remove() {
    if (this.healthBar) {
      this.healthBar.dispose();
    }
    if (this.mesh) {
      this.scene.remove(this.mesh);
    }
    // The vertex-colour material is shared via matte(); only geometries are ours
    this.geometries.forEach(geometry => geometry.dispose());
    this.geometries = [];
  }
}
